// src/pages/common/ReceiptCompletedItemsSidePanelContent.tsx
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClockRotateLeft } from '@/icons';
import { apiGet } from '../../services/api';
import { OrigemConferencia } from '../../constants';
import ScrollableTable from '../../components/common/ScrollableTable';
import { usePaginatedFetch } from '../../hooks/usePaginatedFetch';
import { formatDateTime, formatDate, getEmptyDetailsMessage } from '../../utils/formatUtils';
import CloseButtonSidePanel from '../../components/common/CloseButtonSidePanel';
import Modal from '../../components/common/Modal';
import HistoricoItemModalContent from '../po/HistoricoItemModalContent';
import { ConferenciaFinalizadoItem } from './Conferencia';
import type { HistoricoResponse } from '../../types/compras/Historico';
import './ConferenciaRascunhoItemsSidePanelContent.css';
import { showAlert } from '../../contexts/DialogContext';
import { logger } from '@/utils/logger';

interface ConferenciaFinalizadoItemDetail {
  id: number;
  qtdCompra: number;
  qtdCompraExtra: number;
  qtdCompraOriginal: number;
  removeOrdemCompraItem: boolean;
  requisicaoItemId: number;
  ordemCompraId: number;
  codProduto: string;
  nomeProduto: string;
  codCategoriaProduto: number;
  nomeCategoria: string;
  requisicaoId: number;
  nomePessoaUsuario: string;
  prioridade: string;
  nomeFantasiaLoja: string;
  statusRecebimento: string;
  qtdReceber: number;
  qtdRecebidaTotal: number;
  ordemCompra: boolean;
  qtdRecebida: number;
  tipoRequisicao: string;
  contato: string;
  nomeCliente: string;
  nomeConsultor: string;
  origem: OrigemConferencia;
}

interface ConferenciaFinalizadoDetails {
  dataCriacao: string;
  status: string;
  usuarioId: number;
  nomeUsuario: string;
  codFornecedor: number;
  nomeFornecedor: string;
  lojaEntregaId: number;
  nomeLojaEntrega: string;
  nroNotaFiscal: string;
  itens: {
    content: ConferenciaFinalizadoItemDetail[];
    page: {
      size: number;
      number: number;
      totalElements: number;
      totalPages: number;
    };
  };
}

interface ReceiptCompletedItemsSidePanelContentProps {
  recebimentoFinalizado: ConferenciaFinalizadoItem & { id: number };
  theme: 'dark' | 'light';
  onClose: () => void;
}

const ReceiptCompletedItemsSidePanelContent: React.FC<ReceiptCompletedItemsSidePanelContentProps> = ({
  recebimentoFinalizado,
  theme,
  onClose,
}) => {
  const [header, setHeader] = useState<ConferenciaFinalizadoDetails | null>(null);

  const fetchItems = useCallback(
    async (
      pageNumber: number,
      _currentItems: ConferenciaFinalizadoItemDetail[]
    ): Promise<{ items: ConferenciaFinalizadoItemDetail[]; totalItems: number }> => {
      const queryParams = new URLSearchParams({
        page: pageNumber.toString(),
        size: '10',
        status: 'FINALIZADO', // Parâmetro fixo para itens finalizados
      });
      const endpoint = `/v1/recebimento-conferencia/${recebimentoFinalizado.id}/itens?${queryParams.toString()}`;
      const res = await apiGet(endpoint);
      const details = res.data;
      if (pageNumber === 0) {
        setHeader(details);
      }
      return { items: details.itens.content, totalItems: details.itens.page.totalElements };
    },
    [recebimentoFinalizado.id]
  );

  const [isHistoricoModalOpen, setIsHistoricoModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [historicoData, setHistoricoData] = useState<HistoricoResponse | null>(null);

  const { items, hasMore, isLoading, loadMore, reset } = usePaginatedFetch<ConferenciaFinalizadoItemDetail>({
    fetchFunction: fetchItems,
    pageSize: 10,
    uniqueKey: 'id',
    initialPage: 0,
  });

  const handleViewHistoryClick = async (item: ConferenciaFinalizadoItemDetail) => {
    try {
      const queryParams = new URLSearchParams({
        codProduto: item.codProduto,
        codCategoriaProduto: item.codCategoriaProduto.toString(),
        itemModuloId: item.requisicaoItemId.toString(),
        page: '0',
        size: '10',
      });
      const response = await apiGet(`/v1/historico-itens?${queryParams.toString()}`);
      setHistoricoData(response.data);
      setSelectedItemId(item.requisicaoItemId);
      setIsHistoricoModalOpen(true);
    } catch (error) {
      logger.error('Erro ao carregar histórico:', error);
      if (axios.isAxiosError<{ error?: string }>(error) && error.response?.status === 400 && error.response?.data?.error) {
        await showAlert(error.response.data.error, { variant: 'error' });
      } else {
        await showAlert('Erro ao carregar histórico. Tente novamente.', { variant: 'error' });
      }
    }
  };

  const handleCloseHistoricoModal = () => {
    setIsHistoricoModalOpen(false);
    setHistoricoData(null);
    setSelectedItemId(null);
  };

  useEffect(() => {
    reset();
  }, [recebimentoFinalizado.id, reset]);

  const baseColumns = [
    { header: 'Código', render: (item: ConferenciaFinalizadoItemDetail) => item.codProduto },
    { header: 'Cód. Categoria', render: (item: ConferenciaFinalizadoItemDetail) => item.codCategoriaProduto },
    { header: 'Nome do Produto', render: (item: ConferenciaFinalizadoItemDetail) => item.nomeProduto },
    { header: 'Qtd. Total', render: (item: ConferenciaFinalizadoItemDetail) => item.qtdCompraOriginal },
    { header: 'Requisição', render: (item: ConferenciaFinalizadoItemDetail) => item.requisicaoId },
    { header: 'Ordem de Compra', render: (item: ConferenciaFinalizadoItemDetail) => item.ordemCompraId },
    { header: 'Loja Solicitante', render: (item: ConferenciaFinalizadoItemDetail) => item.nomeFantasiaLoja },
    { header: 'Requisitante', render: (item: ConferenciaFinalizadoItemDetail) => item.nomePessoaUsuario },
    { header: 'Status Recebimento', render: (item: ConferenciaFinalizadoItemDetail) => item.statusRecebimento },
    { header: 'Qtd. Receber', render: (item: ConferenciaFinalizadoItemDetail) => item.qtdReceber },
    { header: 'Qtd. Total Recebido', render: (item: ConferenciaFinalizadoItemDetail) => item.qtdRecebidaTotal },
    { header: 'Qtd. Recebida', render: (item: ConferenciaFinalizadoItemDetail) => item.qtdRecebida },
  ];

  return (
    <div className={`conferencia-items-sidepanel-content ${theme}`}>
      <CloseButtonSidePanel onClick={onClose} className="close-button-absolute"/>
      <div className="title-table">
        <div>
          <div className="facelet-h3">
            <h3>Recebimento Nº {recebimentoFinalizado.id} | Criado em {header ? formatDateTime(header.dataCriacao) : 'Carregando...'}</h3>
          </div>
            <div className="facelet-p">
              <p>Conferente: {header?.nomeUsuario || 'Carregando...'} | Status: {header?.status || 'Carregando...'}</p>
              <p className='p-fornecedor'>Fornecedor: {header?.nomeFornecedor || 'Não informado'} | Loja de entrega: {header?.nomeLojaEntrega} | Nota Fiscal nº: {header?.nroNotaFiscal}</p>
            </div>
        </div>
      </div>
      {isLoading && items.length === 0 ? (
        <p>Carregando itens...</p>
      ) : (
        <ScrollableTable<ConferenciaFinalizadoItemDetail>
          data={items}
          hasMore={hasMore}
          loadMore={loadMore}
          isLoading={isLoading}
          columns={baseColumns}
          onSelectChange={() => {}}
          renderHistoryAction={(item) => (
            <button
              className="action-button"
              onClick={() => handleViewHistoryClick(item)}
              title="Ver Histórico do Item"
            >
              <FontAwesomeIcon icon={faClockRotateLeft} />
            </button>
          )}
          fetchDetails={async (item: ConferenciaFinalizadoItemDetail) => {
            if (!item.tipoRequisicao) return []; // Evita chamada sem tipoRequisicao, retornando vazio.
            const res = await apiGet(`/v1/historico-itens/${item.requisicaoItemId}/po/requisicao-item/detalhes?tipoRequisicao=${item.tipoRequisicao}`);
            return res.data.content;
          }} // Adicionado: fetch para subtabela, replicando exemplo com requisicaoItemId.
          detailsColumns={[
            { header: 'Contato', render: (detail: Record<string, unknown>) => detail.origem === 'PLANEJAMENTO' ? "Planejamento" : String(detail.contato ?? '') },
            { header: 'Cliente', render: (detail: Record<string, unknown>) => detail.origem === 'PLANEJAMENTO' ? "Planejamento" : String(detail.nomeCliente ?? '') },
            { header: 'Consultor', render: (detail: Record<string, unknown>) => detail.origem === 'PLANEJAMENTO' ? "Planejamento" : String(detail.nomeConsultor ?? '') },
            { header: 'Cotação', accessor: 'preOrdem' },
            { header: 'Ordem de Compra', accessor: 'ordemCompra' },
            { header: 'Qtd. Compra', accessor: 'qtdCompraOriginal' },
            { header: 'Qtd. Recebida', accessor: 'qtdRecebida' },
            { header: 'Status Recebimento', accessor: 'statusRecebimento' },
            { header: 'Comprador', accessor: 'comprador' },
            { header: 'Previsão Chegada', accessor: 'previsaoChegada', formatter: formatDate },
            { header: 'Conferência', accessor: 'recebimentoConferencia' },
            { header: 'Recebimento', accessor: 'dataConferencia', formatter: formatDateTime },
            { header: 'Conferente', accessor: 'conferente' },
            { header: 'Nº Nota Fiscal', accessor: 'nroNotaFiscal' },
          ]} // Adicionado: colunas para subtabela, replicando exemplo.
          emptyDetailsMessage={(item: ConferenciaFinalizadoItemDetail) => getEmptyDetailsMessage(item.tipoRequisicao || '')} // Adicionado: mensagem vazia usando util centralizada.
          emptyMessage="Nenhum item encontrado."
          showCheckboxes={false}
        />
      )}
      {isHistoricoModalOpen && historicoData && (
        <Modal
          isOpen={isHistoricoModalOpen}
          onClose={handleCloseHistoricoModal}
          theme={theme}
          zIndex={1002}
        >
          <HistoricoItemModalContent
            historico={historicoData}
            requisicaoItemId={selectedItemId!}
            isOpen={isHistoricoModalOpen}
            onClose={handleCloseHistoricoModal}
            theme={theme}
            showCancelButton={false}
          />
        </Modal>
      )}
    </div>
  );
};

export default ReceiptCompletedItemsSidePanelContent;